import React, { useState, useRef } from "react";
import "../styles/Drones.css"; // Import Drones.css instead
import { useQuery, useMutation, gql } from "@apollo/client";

// GraphQL Definitions
const GET_PARTS = gql`
  query {
    parts {
      partId
      name
      brand
      price
      description
      image
      quantity
      status
      sellerId
      sellerInfo {
        email
        phoneNumber
      }
      weight
      length
      breadth
      height
    }
  }
`;

const UPDATE_PART_STATUS = gql`
  mutation UpdatePartStatus($partId: String!, $status: String!) {
    updatePartStatus(partId: $partId, status: $status) {
      partId
      status
    }
  }
`;

const DELETE_PART = gql`
  mutation DeletePart($partId: String!) {
    deletePart(partId: $partId) {
      partId
      name
    }
  }
`;

function Parts() {
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [expandedSeller, setExpandedSeller] = useState(null);
  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "sellerId", direction: "asc" });
  const [viewingPart, setViewingPart] = useState(null);
  const [viewingDescription, setViewingDescription] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPage, setProductsPage] = useState({});
  const ITEMS_PER_PAGE = 10;
  const PRODUCTS_PER_PAGE = 10;
  const tableRef = useRef(null);

  // Apollo Hooks
  const { loading, error: queryError, data, refetch } = useQuery(GET_PARTS, {
    fetchPolicy: "network-only",
  });

  const [updateStatusMutation] = useMutation(UPDATE_PART_STATUS);
  const [deletePartMutation] = useMutation(DELETE_PART);

  const parts = data?.parts || [];
  const error = queryError ? queryError.message : null;

  // Group parts by seller ID
  const groupedBySeller = parts.reduce((acc, part) => {
    const sellerId = part.sellerId;
    if (!acc[sellerId]) {
      acc[sellerId] = {
        sellerId: sellerId,
        sellerInfo: part.sellerInfo,
        parts: []
      };
    }
    acc[sellerId].parts.push(part);
    return acc;
  }, {});

  // Convert to array and sort
  const sellerGroups = Object.values(groupedBySeller).sort((a, b) =>
    a.sellerId.localeCompare(b.sellerId)
  );

  // Calculate seller stats
  const sellerGroupsWithStats = sellerGroups.map(seller => {
    const totalValue = seller.parts.reduce((sum, p) => sum + (p.price * p.quantity), 0);
    const totalStock = seller.parts.reduce((sum, p) => sum + p.quantity, 0);
    const approvedParts = seller.parts.filter(p => p.status === 'approved').length;
    const pendingParts = seller.parts.filter(p => p.status === 'pending').length;
    const rejectedParts = seller.parts.filter(p => p.status === 'rejected').length;

    return {
      ...seller,
      totalValue,
      totalStock,
      approvedParts,
      pendingParts,
      rejectedParts,
      totalParts: seller.parts.length
    };
  });

  // Filter sellers based on search
  const filteredSellers = sellerGroupsWithStats.filter(seller => {
    const matchesSearch =
      searchTerm === "" ||
      seller.sellerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.sellerInfo?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.sellerInfo?.phoneNumber?.includes(searchTerm);

    if (selectedStatus === "all") return matchesSearch;

    // Filter by status
    const filteredParts = seller.parts.filter(part =>
      part.status.toLowerCase() === selectedStatus
    );

    return matchesSearch && filteredParts.length > 0;
  });

  // Sort sellers
  const sortedSellers = [...filteredSellers].sort((a, b) => {
    const key = sortConfig.key;
    let aVal = a[key];
    let bVal = b[key];

    if (key === 'sellerId' || key === 'email') {
      aVal = aVal?.toLowerCase?.() || '';
      bVal = bVal?.toLowerCase?.() || '';
    }

    if (sortConfig.direction === "asc") {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination for sellers
  const totalFilteredPages = Math.ceil(sortedSellers.length / ITEMS_PER_PAGE);
  const paginatedSellers = sortedSellers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Get filtered parts for expanded seller with pagination
  const getFilteredPartsForSeller = (sellerId) => {
    const seller = groupedBySeller[sellerId];
    if (!seller) return [];

    return seller.parts.filter(part => {
      return selectedStatus === "all" || part.status.toLowerCase() === selectedStatus;
    });
  };

  // Get paginated products for a seller
  const getPaginatedProductsForSeller = (sellerId) => {
    const filteredParts = getFilteredPartsForSeller(sellerId);
    const currentProductsPage = productsPage[sellerId] || 1;
    const startIndex = (currentProductsPage - 1) * PRODUCTS_PER_PAGE;
    const endIndex = startIndex + PRODUCTS_PER_PAGE;

    return {
      products: filteredParts.slice(startIndex, endIndex),
      currentPage: currentProductsPage,
      totalPages: Math.ceil(filteredParts.length / PRODUCTS_PER_PAGE),
      totalProducts: filteredParts.length
    };
  };

  // Handle products page change
  const handleProductsPageChange = (sellerId, page) => {
    setProductsPage(prev => ({
      ...prev,
      [sellerId]: page
    }));
  };

  // Update part status
  const updatePartStatus = async (partId, newStatus) => {
    const updatingIdsCopy = new Set(updatingIds);
    updatingIdsCopy.add(`${partId}-${newStatus}`);
    setUpdatingIds(updatingIdsCopy);

    try {
      await updateStatusMutation({
        variables: { partId, status: newStatus.toLowerCase() },
      });
      refetch();
    } catch (err) {
      alert(`Error updating status: ${err.message}`);
    } finally {
      const updatingIdsCopy = new Set(updatingIds);
      updatingIdsCopy.delete(`${partId}-${newStatus}`);
      setUpdatingIds(updatingIdsCopy);
    }
  };

  // Delete part
  const deletePart = async (partId) => {
    if (!window.confirm("Are you sure you want to delete this part?")) return;

    try {
      await deletePartMutation({
        variables: { partId },
      });
      refetch();
    } catch (err) {
      alert(`Error deleting part: ${err.message}`);
    }
  };



  // View part details with actions
  const viewPartDetails = (part) => {
    setViewingPart(part);
  };

  // Close part details modal
  const closePartDetails = () => {
    setViewingPart(null);
  };

  // View description only
  const viewDescription = (part) => {
    setViewingDescription(part);
  };

  // Close description modal
  const closeDescription = () => {
    setViewingDescription(null);
  };



  // Handle seller page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (tableRef.current) {
      tableRef.current.scrollLeft = 0;
    }
  };

  const statusStats = {
    all: parts.length,
    approved: parts.filter(p => p.status === "approved").length,
    pending: parts.filter(p => p.status === "pending").length,
    rejected: parts.filter(p => p.status === "rejected").length,
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return <span style={{ marginLeft: '4px', fontSize: '12px' }}>{sortConfig.direction === "asc" ? "↑" : "↓"}</span>;
  };

  const truncateDescription = (description, length = 30) => {
    if (!description) return "No description";
    if (description.length <= length) return description;
    return description.substring(0, length) + "...";
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Calculate overall stats
  const totalValue = parts.reduce((sum, p) => sum + (p.price * p.quantity), 0);
  const totalStock = parts.reduce((sum, p) => sum + p.quantity, 0);
  const pendingParts = parts.filter(p => p.status === 'pending').length;

  return (
    <div className="drones-container">
      <div className="main-wrapper">
        {/* Top Navigation */}
        <div className="top-navigation">
          <div className="nav-left">
            <h1>Parts Management System</h1>
            <p>Admin Dashboard • Manage Seller Parts • Real-time Updates</p>
          </div>
          <div className="nav-right">
            <button
              className="btn btn-secondary"
              onClick={() => refetch()}
              disabled={loading}
            >
              {loading ? "🔄 Refreshing..." : "🔄 Refresh"}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                const headers = ["Part ID", "Seller ID", "Name", "Brand", "Price", "Quantity", "Status", "Description"];
                const rows = parts.map(part => [
                  part.partId,
                  part.sellerId,
                  part.name,
                  part.brand,
                  part.price,
                  part.quantity,
                  part.status,
                  part.description
                ]);

                let csvContent = headers.join(',') + '\n';
                rows.forEach(row => {
                  csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
                });

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `parts_export_${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              📊 Export Report
            </button>
          </div>
        </div>

        {/* Compact Stats Dashboard */}
        <div className="compact-stats-container">
          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Inventory Value</div>
              <div className="compact-stat-value">₹{totalValue.toLocaleString('en-IN')}</div>

            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Stock Units</div>
              <div className="compact-stat-value">{totalStock}</div>

            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Sellers</div>
              <div className="compact-stat-value">{sellerGroups.length}</div>

            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Pending Approval</div>
              <div className="compact-stat-value">{pendingParts}</div>

            </div>
          </div>
        </div>

        {/* Control Panel */}
        <div className="control-panel">
          <div className="panel-header">
            <h2 className="panel-title">Parts Management</h2>
          </div>

          <div className="seller-selector">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search sellers by ID, email, or phone..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
              />

            </div>

            <div className="status-tabs-container">
              {["all", "approved", "pending", "rejected"].map((status) => (
                <button
                  key={status}
                  className={`status-tab ${selectedStatus === status ? "active" : ""}`}
                  onClick={() => {
                    setSelectedStatus(status);
                    setCurrentPage(1);
                    // Reset product pages when status changes
                    setProductsPage({});
                  }}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                  <span className="tab-count">{statusStats[status]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <span>{error}</span>
            <button onClick={() => refetch()}>✕</button>
          </div>
        )}

        {/* Main Content Area */}
        <div className="content-area">
          {/* Sellers Table Container */}
          <div className="table-container">
            <div className="table-header">
              <h3 className="table-title">
                All Sellers ({sellerGroups.length})
              </h3>
              <div className="table-controls">
                <div className="table-info">
                  Page {currentPage} of {totalFilteredPages || 1} • Showing {paginatedSellers.length} sellers
                </div>
              </div>
            </div>

            <div className="table-content" ref={tableRef}>
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading seller data...</p>
                </div>
              ) : sellerGroups.length === 0 ? (
                <div className="empty-state">
                  <p>No sellers found</p>
                </div>
              ) : (
                <table className="sellers-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("sellerId")}>
                        Seller ID <SortIcon column="sellerId" />
                      </th>
                      <th onClick={() => handleSort("email")}>
                        Contact Info <SortIcon column="email" />
                      </th>
                      <th onClick={() => handleSort("totalParts")}>
                        Parts <SortIcon column="totalParts" />
                      </th>
                      <th onClick={() => handleSort("totalValue")}>
                        Inventory Value <SortIcon column="totalValue" />
                      </th>
                      <th onClick={() => handleSort("totalStock")}>
                        Total Stock <SortIcon column="totalStock" />
                      </th>
                      <th>Status Breakdown</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSellers.map((seller) => {
                      const { products, currentPage: productsCurrentPage, totalPages: productsTotalPages, totalProducts } = getPaginatedProductsForSeller(seller.sellerId);

                      return (
                        <React.Fragment key={seller.sellerId}>
                          <tr
                            className={`seller-row ${expandedSeller === seller.sellerId ? 'expanded' : ''}`}
                            onClick={() => setExpandedSeller(
                              expandedSeller === seller.sellerId ? null : seller.sellerId
                            )}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <div className="seller-id-cell">
                                <span className="expand-icon">
                                  {expandedSeller === seller.sellerId ? '▼' : '▶'}
                                </span>
                                <strong>Seller #{seller.sellerId}</strong>
                              </div>
                            </td>
                            <td>
                              <div className="seller-contact-cell">
                                <div className="seller-email">
                                  📧 {seller.sellerInfo?.email || "No email"}
                                </div>
                                <div className="seller-phone">
                                  📱 {seller.sellerInfo?.phoneNumber || "No phone"}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="seller-products-count">
                                <span className="count-badge">{seller.totalParts}</span>
                                <span className="count-label">parts</span>
                              </div>
                            </td>
                            <td>
                              <div className="seller-value">
                                <strong className="value-amount">
                                  ₹{seller.totalValue.toLocaleString('en-IN')}
                                </strong>
                              </div>
                            </td>
                            <td>
                              <div className="seller-stock">
                                <span className="stock-count">{seller.totalStock}</span>
                                <span className="stock-label">units</span>
                              </div>
                            </td>
                            <td>
                              <div className="status-breakdown">
                                <span className="status-dot approved" title={`${seller.approvedParts} approved`}>
                                  {seller.approvedParts}
                                </span>
                                <span className="status-dot pending" title={`${seller.pendingParts} pending`}>
                                  {seller.pendingParts}
                                </span>
                                <span className="status-dot rejected" title={`${seller.rejectedParts} rejected`}>
                                  {seller.rejectedParts}
                                </span>
                              </div>
                            </td>
                            <td>
                              <button
                                className="action-btn action-view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedSeller(
                                    expandedSeller === seller.sellerId ? null : seller.sellerId
                                  );
                                }}
                                title="View Parts"
                              >
                                {expandedSeller === seller.sellerId ? '👁️‍🗨️' : '👁️'}
                              </button>
                            </td>
                          </tr>

                          {/* Expanded Parts Row */}
                          {expandedSeller === seller.sellerId && (
                            <tr className="products-row">
                              <td colSpan="7">
                                <div className="products-container">
                                  <div className="products-header">
                                    <div>
                                      <h4>Parts from Seller #{seller.sellerId}</h4>
                                      <span className="products-count">
                                        {totalProducts} parts • Showing {products.length} on page {productsCurrentPage} of {productsTotalPages}
                                      </span>
                                    </div>
                                  </div>

                                  {totalProducts === 0 ? (
                                    <div className="no-products">
                                      <p>No parts found for the selected status filter.</p>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="products-table-container">
                                        <table className="products-table">
                                          <thead>
                                            <tr>
                                              <th>Part ID</th>
                                              <th>Part Details</th>
                                              <th>Brand</th>
                                              <th>Price</th>
                                              <th>Stock</th>
                                              <th>Status</th>
                                              <th>Description</th>
                                              <th>Actions</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {products.map((part) => (
                                              <tr key={part.partId}>
                                                <td>
                                                  <div className="id-cell">
                                                    {part.partId}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-details-cell">
                                                    {part.image && (
                                                      <img src={part.image} alt={part.name} className="drone-image" />
                                                    )}
                                                    <div>
                                                      <div className="drone-name">{part.name}</div>
                                                      <div className="drone-specs">
                                                        <span className="drone-brand">{part.brand}</span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-brand">{part.brand}</div>
                                                </td>
                                                <td>
                                                  <div className="price-cell">
                                                    {formatPrice(part.price)}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className={`quantity-cell ${part.quantity === 0 ? 'out-of-stock' : ''}`}>
                                                    {part.quantity}
                                                    {part.quantity < 10 && part.quantity > 0 && (
                                                      <span className="low-stock-indicator">!</span>
                                                    )}
                                                  </div>
                                                </td>
                                                <td>
                                                  <span className={`status-badge status-${part.status}`}>
                                                    {part.status.toUpperCase()}
                                                  </span>
                                                </td>
                                                <td>
                                                  <div>
                                                    {truncateDescription(part.description, 30)}
                                                    {part.description && part.description.length > 30 && (
                                                      <button
                                                        onClick={() => viewDescription(part)}
                                                        className="read-more-btn"
                                                      >
                                                        Read more
                                                      </button>
                                                    )}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="action-buttons">
                                                    {part.status === "pending" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updatePartStatus(part.partId, "approved")}
                                                          disabled={updatingIds.has(`${part.partId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${part.partId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updatePartStatus(part.partId, "rejected")}
                                                          disabled={updatingIds.has(`${part.partId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${part.partId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {part.status === "approved" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updatePartStatus(part.partId, "pending")}
                                                          disabled={updatingIds.has(`${part.partId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${part.partId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updatePartStatus(part.partId, "rejected")}
                                                          disabled={updatingIds.has(`${part.partId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${part.partId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {part.status === "rejected" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updatePartStatus(part.partId, "pending")}
                                                          disabled={updatingIds.has(`${part.partId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${part.partId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updatePartStatus(part.partId, "approved")}
                                                          disabled={updatingIds.has(`${part.partId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${part.partId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                      </>
                                                    )}

                                                    <button
                                                      className="action-btn action-view"
                                                      onClick={() => viewPartDetails(part)}
                                                      title="View Details"
                                                    >
                                                      👁
                                                    </button>



                                                    <button
                                                      className="action-btn action-delete"
                                                      onClick={() => deletePart(part.partId)}
                                                      title="Delete"
                                                    >
                                                      🗑
                                                    </button>
                                                  </div>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>

                                      {/* Products Pagination */}
                                      {productsTotalPages > 1 && (
                                        <div className="products-pagination">
                                          <button
                                            onClick={() => handleProductsPageChange(seller.sellerId, productsCurrentPage - 1)}
                                            disabled={productsCurrentPage === 1}
                                            className="pagination-btn"
                                          >
                                            &lt; Previous
                                          </button>
                                          <div className="pagination-info">
                                            Page {productsCurrentPage} of {productsTotalPages}
                                          </div>
                                          <button
                                            onClick={() => handleProductsPageChange(seller.sellerId, productsCurrentPage + 1)}
                                            disabled={productsCurrentPage === productsTotalPages}
                                            className="pagination-btn"
                                          >
                                            Next &gt;
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls for Sellers */}
            {totalFilteredPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
                {Array.from({ length: totalFilteredPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    className={currentPage === num ? "active" : ""}
                    onClick={() => handlePageChange(num)}
                  >
                    {num}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalFilteredPages}
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Part Details Modal with Actions */}
      {viewingPart && (
        <div className="modal-overlay" onClick={closePartDetails}>
          <div className="modal-content drone-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Part Details</h2>
              <button className="close-btn" onClick={closePartDetails}>×</button>
            </div>
            <div className="modal-body">
              <div className="drone-details-grid">
                <div className="drone-image-section">
                  {viewingPart.image ? (
                    <img
                      src={viewingPart.image}
                      alt={viewingPart.name}
                      className="drone-detail-image"
                    />
                  ) : (
                    <div className="no-image-placeholder">No Image</div>
                  )}
                </div>

                <div className="drone-info-section">
                  <div className="info-row">
                    <div className="info-label">Part ID</div>
                    <div className="info-value">{viewingPart.partId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Name</div>
                    <div className="info-value">{viewingPart.name}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Brand</div>
                    <div className="info-value">{viewingPart.brand}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Type</div>
                    <div className="info-value">Drone Part</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Price</div>
                    <div className="info-value price-value">{formatPrice(viewingPart.price)}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Dimensions</div>
                    <div className="info-value">
                      {viewingPart.length || 0} x {viewingPart.breadth || 0} x {viewingPart.height || 0} cm
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Weight</div>
                    <div className="info-value">{viewingPart.weight || 0} kg</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Quantity</div>
                    <div className="info-value">
                      <span className={`quantity-display ${viewingPart.quantity === 0 ? 'out-of-stock' : ''}`}>
                        {viewingPart.quantity}
                      </span>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Status</div>
                    <div className="info-value">
                      <span className={`status-badge status-${viewingPart.status}`}>
                        {viewingPart.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller ID</div>
                    <div className="info-value">{viewingPart.sellerId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller Email</div>
                    <div className="info-value">{viewingPart.sellerInfo?.email || "N/A"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller Phone</div>
                    <div className="info-value">{viewingPart.sellerInfo?.phoneNumber || "N/A"}</div>
                  </div>
                </div>

                <div className="drone-description-section">
                  <div className="info-label">Description</div>
                  <div className="description-content">
                    {viewingPart.description || 'No description available'}
                  </div>
                </div>

                {/* Actions in Details Modal */}
                <div className="drone-actions-section">
                  <div className="info-label">Quick Actions</div>
                  <div className="action-buttons-grid">
                    {viewingPart.status === "pending" && (
                      <>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updatePartStatus(viewingPart.partId, "approved");
                            closePartDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPart.partId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingPart.partId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updatePartStatus(viewingPart.partId, "rejected");
                            closePartDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPart.partId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingPart.partId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                      </>
                    )}

                    {viewingPart.status === "approved" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updatePartStatus(viewingPart.partId, "pending");
                            closePartDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPart.partId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingPart.partId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updatePartStatus(viewingPart.partId, "rejected");
                            closePartDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPart.partId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingPart.partId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                      </>
                    )}

                    {viewingPart.status === "rejected" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updatePartStatus(viewingPart.partId, "pending");
                            closePartDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPart.partId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingPart.partId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updatePartStatus(viewingPart.partId, "approved");
                            closePartDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPart.partId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingPart.partId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                      </>
                    )}



                    <button
                      className="action-btn-large action-delete"
                      onClick={() => {
                        if (window.confirm("Are you sure you want to delete this part?")) {
                          deletePart(viewingPart.partId);
                          closePartDetails();
                        }
                      }}
                      title="Delete"
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Description Modal */}
      {viewingDescription && (
        <div className="modal-overlay" onClick={closeDescription}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Description</h2>
              <button className="close-btn" onClick={closeDescription}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ lineHeight: '1.6', margin: 0, whiteSpace: 'pre-wrap' }}>
                {viewingDescription.description}
              </p>
            </div>
          </div>
        </div>
      )}



    </div>
  );
}

export default Parts;